import { useTranslation } from 'react-i18next';
import { EconomicAccountingReport, ReportFilterEconomicAccountingGroupBy } from '../../types';
import { commonColProps, DataGridHandler } from '../DataGridFlexSales';
import { FormulaTooltip } from './formulaTooltip';

export interface EconomicAccountingReportDataGridContext {
  t: (key: string | string[]) => string;
  groupBy: ReportFilterEconomicAccountingGroupBy[];
  country: string;
}

const initialContext: EconomicAccountingReportDataGridContext = {
  t: () => '',
  groupBy: [],
  country: '',
};

export default (locale: Locale) => {
  const handler = new DataGridHandler<
    EconomicAccountingReport,
    EconomicAccountingReportDataGridContext
  >(initialContext, locale);
  const { t } = useTranslation();
  const DifferenceTooltip = FormulaTooltip({
    description: t('table.columns.difference.description'),
  });
  const GrossSaleTooltip = t('table.columns.venta_bruta_tooltip');
  handler.addColumn({
    accessor: 'country',
    type: 'string',
    title: ({ t }) => t('table.columns.country'),
    ...commonColProps.country,
  });

  handler.addColumn({
    accessor: 'store',
    type: 'string',
    title: ({ t, country }) => t([`table.columns.${country}.store`, 'table.columns.store']),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return false;
    },
  });

  handler.addColumn({
    accessor: 'franchiseType',
    type: 'string',
    title: ({ t }) => t('table.columns.franchise_type'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return false;
    },
  });

  handler.addColumn({
    accessor: 'businessDate',
    type: 'date',
    title: ({ t }) => t('table.columns.business_date'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return groupBy.includes('businessDate');
    },
  });

  handler.addColumn({
    accessor: 'tenderName',
    type: 'string',
    title: ({ t }) => t('table.columns.tender_name'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return groupBy.includes('paymentTypeId');
    },
  });

  handler.addColumn({
    accessor: 'paymentType',
    type: 'string',
    title: ({ t }) => t('table.columns.payment_type'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return groupBy.includes('paymentTypeId');
    },
  });

  handler.addColumn({
    accessor: 'account',
    type: 'string',
    title: ({ t }) => t('table.columns.account'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return groupBy.includes('account');
    },
  });

  handler.addColumn({
    accessor: 'subAccount',
    type: 'string',
    title: ({ t }) => t('table.columns.sub_account'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return groupBy.includes('subaccount');
    },
  });

  handler.addColumn({
    accessor: 'grossSale',
    type: 'number',
    tooltip: GrossSaleTooltip,
    title: ({ t }) => t('table.columns.venta_bruta'),
  });

  handler.addColumn({
    accessor: 'cashierDifference',
    type: 'number',
    title: ({ t }) => t('table.columns.cashier_difference'),
  });

  handler.addColumn({
    accessor: 'excess',
    type: 'number',
    title: ({ t }) => t('table.columns.exceso'),
  });

  handler.addColumn({
    accessor: 'tips',
    type: 'number',
    title: ({ t }) => t('table.columns.tips'),
  });

  handler.addColumn({
    accessor: 'pettyCashExtraction',
    type: 'number',
    title: ({ t }) => t('table.columns.petty_cash_extraction'),
  });

  handler.addColumn({
    accessor: 'recaudado',
    type: 'string',
    title: ({ t }) => t('table.columns.recaudado'),
    ...commonColProps.recaudado,
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return false;
    },
  });

  handler.addColumn({
    accessor: 'theoricalBalanceReca',
    type: 'number',
    title: ({ t }) => t('table.columns.theorical_balance_reca'),
  });

  handler.addColumn({
    accessor: 'theoricalBalanceConvReca',
    type: 'number',
    title: ({ t }) => t('table.columns.theorical_balance_conv_reca'),
  });

  handler.addColumn({
    accessor: 'extraCash',
    type: 'number',
    title: ({ t }) => t('table.columns.extra_cash'),
  });

  handler.addColumn({
    accessor: 'difference',
    tooltip: DifferenceTooltip,
    type: 'number',
    title: ({ t }) => t('table.columns.difference.title'),
  });

  handler.addColumn({
    accessor: 'reprocessed',
    type: 'string',
    title: ({ t }) => t('table.columns.reprocessed'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return false;
    },
  });

  handler.addColumn({
    accessor: 'comment',
    type: 'string',
    title: ({ t }) => t('table.columns.comment'),
    available: ({ groupBy }) => {
      if (groupBy.length === 1 && groupBy[0] === 'countryAcronym') return true;
      return false;
    },
  });

  return handler;
};
